// Box rejects / \ : * ? " < > |, control chars, empty, `.`, and `..`.
// Unicode stays so patient names on DICOM exports are not rewritten.
const BOX_INVALID_NAME_CHARS = /[\/\\:*?"<>|\x00-\x1f\x7f]/g;

const SIMPLE_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;
const FILE_CONCURRENCY = 3;
const PART_CONCURRENCY = 3;

export type BoxUploadItem = {
  file: File;
  /** Path inside the case folder, including the file name. `/` separators. */
  relativePath?: string;
  /** Stable id used to report progress and match failures in the UI. */
  sourceKey?: string;
};

type NormalizedUpload = {
  file: File;
  relativePath: string;
  sourceKey: string;
  leafName: string;
};

const sanitizeName = (name: string) => {
  let sanitized = name.trim().replace(BOX_INVALID_NAME_CHARS, "_");
  sanitized = sanitized.replace(/^\.+$/, "_");
  if (!sanitized || sanitized === "." || sanitized === "..") sanitized = "_";
  if (sanitized.length <= 255) return sanitized;

  const lastDot = sanitized.lastIndexOf(".");
  const ext = lastDot > 0 ? sanitized.slice(lastDot) : "";
  const base = lastDot > 0 ? sanitized.slice(0, lastDot) : sanitized;
  const room = Math.max(1, 255 - Math.min(ext.length, 40));
  return `${base.slice(0, room)}${ext.slice(0, 40)}`.slice(0, 255);
};

const splitFileName = (filename: string) => {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0) return { name: filename, ext: "" };
  return {
    name: filename.slice(0, lastDot),
    ext: filename.slice(lastDot),
  };
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const mapPool = async <T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  const results = new Array<R>(items.length);
  let cursor = 0;
  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (cursor < items.length) {
        const index = cursor++;
        results[index] = await fn(items[index], index);
      }
    },
  );
  await Promise.all(workers);
  return results;
};

const boxErrorMessage = (status: number, body: string, fallback: string) => {
  try {
    const data = JSON.parse(body);
    return `${status} - ${data.message || data.code || fallback}`;
  } catch {
    return `${status} - ${body || fallback}`;
  }
};

const normalizeUpload = (item: File | BoxUploadItem, index: number): NormalizedUpload => {
  const file = item instanceof File ? item : item.file;
  const rawPath =
    item instanceof File
      ? file.webkitRelativePath || file.name
      : item.relativePath || file.webkitRelativePath || file.name;
  const relativePath = rawPath.replace(/\\/g, "/").replace(/^\/+/, "");
  const leafName = relativePath.split("/").filter(Boolean).pop() || file.name;
  const sourceKey =
    item instanceof File
      ? `${relativePath}::${file.size}::${file.lastModified}::${index}`
      : item.sourceKey ||
        `${relativePath}::${file.size}::${file.lastModified}::${index}`;
  return { file, relativePath, sourceKey, leafName };
};

// ─── Incremental SHA-1 (Box part and commit digests) ─────────────

class Sha1 {
  private h0 = 0x67452301;
  private h1 = 0xefcdab89;
  private h2 = 0x98badcfe;
  private h3 = 0x10325476;
  private h4 = 0xc3d2e1f0;
  private block = new Uint8Array(64);
  private blockLen = 0;
  private bytesHi = 0;
  private bytesLo = 0;

  update(data: Uint8Array) {
    const added = this.bytesLo + data.length;
    this.bytesLo = added >>> 0;
    this.bytesHi = (this.bytesHi + Math.floor(added / 0x100000000)) >>> 0;

    let offset = 0;
    while (offset < data.length) {
      const take = Math.min(64 - this.blockLen, data.length - offset);
      this.block.set(data.subarray(offset, offset + take), this.blockLen);
      this.blockLen += take;
      offset += take;
      if (this.blockLen === 64) {
        this.process(this.block);
        this.blockLen = 0;
      }
    }
  }

  digest(): Uint8Array {
    const bitLo = (this.bytesLo << 3) >>> 0;
    const bitHi = ((this.bytesHi << 3) | (this.bytesLo >>> 29)) >>> 0;

    this.block[this.blockLen++] = 0x80;
    if (this.blockLen > 56) {
      this.block.fill(0, this.blockLen);
      this.process(this.block);
      this.blockLen = 0;
    }
    this.block.fill(0, this.blockLen, 56);
    const view = new DataView(this.block.buffer, this.block.byteOffset, 64);
    view.setUint32(56, bitHi);
    view.setUint32(60, bitLo);
    this.process(this.block);

    const out = new Uint8Array(20);
    const outView = new DataView(out.buffer);
    outView.setUint32(0, this.h0);
    outView.setUint32(4, this.h1);
    outView.setUint32(8, this.h2);
    outView.setUint32(12, this.h3);
    outView.setUint32(16, this.h4);
    return out;
  }

  private process(block: Uint8Array) {
    const w = new Uint32Array(80);
    const view = new DataView(block.buffer, block.byteOffset, 64);
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(i * 4);
    for (let i = 16; i < 80; i++) {
      const n = w[i - 3] ^ w[i - 8] ^ w[i - 14] ^ w[i - 16];
      w[i] = ((n << 1) | (n >>> 31)) >>> 0;
    }

    let a = this.h0;
    let b = this.h1;
    let c = this.h2;
    let d = this.h3;
    let e = this.h4;

    for (let i = 0; i < 80; i++) {
      let f: number;
      let k: number;
      if (i < 20) {
        f = (b & c) | (~b & d);
        k = 0x5a827999;
      } else if (i < 40) {
        f = b ^ c ^ d;
        k = 0x6ed9eba1;
      } else if (i < 60) {
        f = (b & c) | (b & d) | (c & d);
        k = 0x8f1bbcdc;
      } else {
        f = b ^ c ^ d;
        k = 0xca62c1d6;
      }
      const temp =
        ((((a << 5) | (a >>> 27)) + f + e + k + w[i]) >>> 0);
      e = d;
      d = c;
      c = ((b << 30) | (b >>> 2)) >>> 0;
      b = a;
      a = temp;
    }

    this.h0 = (this.h0 + a) >>> 0;
    this.h1 = (this.h1 + b) >>> 0;
    this.h2 = (this.h2 + c) >>> 0;
    this.h3 = (this.h3 + d) >>> 0;
    this.h4 = (this.h4 + e) >>> 0;
  }
}

const bytesToBase64 = (bytes: Uint8Array) => {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};

const sha1Base64 = (bytes: Uint8Array) => {
  const hash = new Sha1();
  hash.update(bytes);
  return bytesToBase64(hash.digest());
};

const hashFileBase64 = async (file: File) => {
  const hash = new Sha1();
  const chunkSize = 8 * 1024 * 1024;
  for (let offset = 0; offset < file.size; offset += chunkSize) {
    const buf = new Uint8Array(
      await file.slice(offset, Math.min(offset + chunkSize, file.size)).arrayBuffer(),
    );
    hash.update(buf);
  }
  return bytesToBase64(hash.digest());
};

// ─── Folder listing and unique names ──────────────────────────────

type BoxEntry = { type?: string; id?: string; name?: string };

export const isBoxAuthFailure = (
  error?: unknown,
  failedFiles?: Array<{ error?: string }>,
) => {
  const texts = [
    error instanceof Error ? error.message : String(error || ""),
    ...(failedFiles || []).map((file) => file.error || ""),
  ];
  return texts.some((text) =>
    /401|403|unauthorized|authorization failed|failed to fetch|networkerror|load failed/i.test(
      text,
    ),
  );
};

const listFolderEntries = async (
  folderId: string,
  accessToken: string,
): Promise<BoxEntry[]> => {
  const entries: BoxEntry[] = [];
  let marker = "";

  do {
    const url = new URL(`https://api.box.com/2.0/folders/${folderId}/items`);
    url.searchParams.set("limit", "1000");
    url.searchParams.set("fields", "name,id,type");
    url.searchParams.set("usemarker", "true");
    if (marker) url.searchParams.set("marker", marker);

    let res: Response;
    try {
      res = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    } catch {
      // Box 401 responses omit CORS headers, so the browser reports this
      // as a network failure instead of Unauthorized.
      throw new Error("Box authorization failed");
    }
    if (res.status === 401 || res.status === 403) {
      throw new Error("Box authorization failed");
    }
    if (!res.ok) {
      throw new Error(
        boxErrorMessage(res.status, await res.text(), "Failed to list folder"),
      );
    }
    const data = await res.json();
    entries.push(...(data.entries || []));
    marker = data.next_marker || "";
  } while (marker);

  return entries;
};

const conflictId = (body: string): string | null => {
  try {
    const data = JSON.parse(body);
    const conflicts = data?.context_info?.conflicts;
    const conflict = Array.isArray(conflicts) ? conflicts[0] : conflicts;
    if (conflict?.id && conflict.type !== "file") return String(conflict.id);
  } catch {
    /* ignore */
  }
  return null;
};

export const generateUniqueFileName = async (
  folderId: string,
  originalName: string,
  accessToken: string,
): Promise<string> => {
  const entries = await listFolderEntries(folderId, accessToken);
  const existing = new Set(entries.map((entry) => entry.name).filter(Boolean) as string[]);
  return reserveName(existing, originalName);
};

const reserveName = (existing: Set<string>, originalName: string) => {
  const clean = sanitizeName(originalName);
  const { name: baseName, ext } = splitFileName(clean);
  if (!existing.has(clean)) {
    existing.add(clean);
    return clean;
  }

  let counter = 1;
  let candidate = sanitizeName(`${baseName}(${counter})${ext}`);
  while (existing.has(candidate) && counter < 1000) {
    counter++;
    candidate = sanitizeName(`${baseName}(${counter})${ext}`);
  }
  if (existing.has(candidate)) {
    candidate = sanitizeName(`${baseName}-${Date.now()}${ext}`);
  }
  existing.add(candidate);
  return candidate;
};

const nameListCache = new Map<string, Promise<Set<string>>>();

const namesInFolder = (folderId: string, accessToken: string) => {
  const cached = nameListCache.get(folderId);
  if (cached) return cached;

  const pending = listFolderEntries(folderId, accessToken)
    .then(
      (entries) =>
        new Set(entries.map((entry) => entry.name).filter(Boolean) as string[]),
    )
    .catch((error) => {
      nameListCache.delete(folderId);
      throw error;
    });
  nameListCache.set(folderId, pending);
  return pending;
};

const reserveUniqueFileName = async (
  folderId: string,
  originalName: string,
  accessToken: string,
) => reserveName(await namesInFolder(folderId, accessToken), originalName);

// ─── Upload one file ──────────────────────────────────────────────

const xhrSend = (
  method: "POST" | "PUT",
  url: string,
  headers: Record<string, string>,
  body: Document | XMLHttpRequestBodyInit | null,
  onBytes?: (loaded: number) => void,
) =>
  new Promise<{ status: number; text: string; retryAfter: number }>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    Object.entries(headers).forEach(([key, value]) => xhr.setRequestHeader(key, value));
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onBytes) onBytes(event.loaded);
    };
    xhr.onload = () => {
      const retryAfter = Number(xhr.getResponseHeader("Retry-After"));
      resolve({
        status: xhr.status,
        text: xhr.responseText || "",
        retryAfter: Number.isFinite(retryAfter) ? retryAfter : 1,
      });
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(body);
  });

const isRetryable = (error: unknown) => {
  const message = error instanceof Error ? error.message : "";
  return message === "Network error" || /^429\b/.test(message) || /^5\d\d\b/.test(message);
};

const withRetry = async <T>(fn: () => Promise<T>) => {
  let lastError: unknown;
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (!isRetryable(error) || attempt === 3) throw error;
      await sleep(Math.min(400 * 2 ** attempt, 8000));
    }
  }
  throw lastError;
};

const uploadSmallFile = async (
  file: File,
  folderId: string,
  fileName: string,
  accessToken: string,
  onProgress: (percent: number) => void,
) => {
  const formData = new FormData();
  formData.append(
    "attributes",
    JSON.stringify({ name: fileName, parent: { id: folderId } }),
  );
  formData.append("file", file, fileName);

  const response = await xhrSend(
    "POST",
    "https://upload.box.com/api/2.0/files/content",
    { Authorization: `Bearer ${accessToken}` },
    formData,
    (loaded) => onProgress(Math.round((loaded / Math.max(file.size, 1)) * 100)),
  );

  if (response.status >= 200 && response.status < 300) {
    const data = JSON.parse(response.text);
    return data.entries?.[0]?.id as string | undefined;
  }
  throw new Error(boxErrorMessage(response.status, response.text, "Upload failed"));
};

const uploadLargeFile = async (
  file: File,
  folderId: string,
  fileName: string,
  accessToken: string,
  onProgress: (percent: number) => void,
) => {
  const sessionRes = await fetch("https://upload.box.com/api/2.0/files/upload_sessions", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      folder_id: folderId,
      file_size: file.size,
      file_name: fileName,
    }),
  });

  if (!sessionRes.ok) {
    throw new Error(
      boxErrorMessage(sessionRes.status, await sessionRes.text(), "Could not start upload"),
    );
  }

  const session = await sessionRes.json();
  const partSize = Number(session.part_size);
  const uploadPartUrl = session.session_endpoints?.upload_part as string;
  const commitUrl = session.session_endpoints?.commit as string;
  const abortUrl = session.session_endpoints?.abort as string;
  if (!partSize || !uploadPartUrl || !commitUrl) {
    throw new Error("Box did not return a usable upload session");
  }

  const totalParts = Math.ceil(file.size / partSize);
  const partLoaded = new Array<number>(totalParts).fill(0);
  const report = () => {
    const loaded = partLoaded.reduce((sum, value) => sum + value, 0);
    onProgress(Math.min(99, Math.round((loaded / file.size) * 100)));
  };

  const hashPromise = hashFileBase64(file);

  try {
    const parts = await mapPool(
      Array.from({ length: totalParts }, (_, index) => index),
      PART_CONCURRENCY,
      async (index) => {
        const start = index * partSize;
        const end = Math.min(start + partSize, file.size);
        const bytes = new Uint8Array(await file.slice(start, end).arrayBuffer());
        const digest = sha1Base64(bytes);

        const response = await withRetry(async () => {
          const result = await xhrSend(
            "PUT",
            uploadPartUrl,
            {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/octet-stream",
              Digest: `sha=${digest}`,
              "Content-Range": `bytes ${start}-${end - 1}/${file.size}`,
            },
            bytes,
            (loaded) => {
              partLoaded[index] = Math.min(loaded, end - start);
              report();
            },
          );
          if (result.status === 429 || result.status >= 500) {
            await sleep(Math.max(result.retryAfter, 1) * 1000);
            throw new Error(boxErrorMessage(result.status, result.text, "Part upload failed"));
          }
          if (result.status < 200 || result.status >= 300) {
            throw new Error(boxErrorMessage(result.status, result.text, "Part upload failed"));
          }
          return result;
        });

        partLoaded[index] = end - start;
        report();
        const part = JSON.parse(response.text).part;
        return {
          part_id: part.part_id as string,
          offset: start,
          size: end - start,
        };
      },
    );

    const digest = await hashPromise;
    parts.sort((a, b) => a.offset - b.offset);

    let commitText = "";
    let commitStatus = 0;
    for (let attempt = 0; attempt < 8; attempt++) {
      const commit = await xhrSend(
        "POST",
        commitUrl,
        {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
          Digest: `sha=${digest}`,
        },
        JSON.stringify({ parts }),
      );
      commitStatus = commit.status;
      commitText = commit.text;
      if (commit.status === 202) {
        await sleep(Math.max(commit.retryAfter, 1) * 1000);
        continue;
      }
      break;
    }

    if (commitStatus < 200 || commitStatus >= 300) {
      throw new Error(boxErrorMessage(commitStatus, commitText, "Could not finish upload"));
    }

    const data = JSON.parse(commitText);
    onProgress(100);
    return (data.entries?.[0]?.id || data.id) as string | undefined;
  } catch (error) {
    await hashPromise.catch(() => undefined);
    if (abortUrl) {
      fetch(abortUrl, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${accessToken}` },
      }).catch(() => undefined);
    }
    throw error;
  }
};

const uploadOneFile = async (
  file: File,
  folderId: string,
  fileName: string,
  accessToken: string,
  onProgress: (percent: number) => void,
) => {
  if (file.size >= SIMPLE_UPLOAD_MAX_BYTES) {
    return uploadLargeFile(file, folderId, fileName, accessToken, onProgress);
  }
  return withRetry(() =>
    uploadSmallFile(file, folderId, fileName, accessToken, onProgress),
  );
};

export const uploadToBoxWithProgress = (
  file: File,
  folderId: string,
  accessToken: string,
  onProgress: (percent: number) => void,
) =>
  new Promise<{
    success: boolean;
    boxFileId?: string;
    fileName: string;
    error?: string;
  }>(async (resolve) => {
    try {
      const uniqueFileName = await reserveUniqueFileName(folderId, file.name, accessToken);
      const boxFileId = await uploadOneFile(
        file,
        folderId,
        uniqueFileName,
        accessToken,
        onProgress,
      );
      if (!boxFileId) {
        resolve({ success: false, fileName: uniqueFileName, error: "Box did not return a file id" });
        return;
      }
      resolve({ success: true, boxFileId, fileName: uniqueFileName });
    } catch (error) {
      resolve({
        success: false,
        fileName: file.name,
        error: error instanceof Error ? error.message : "Upload failed",
      });
    }
  });

// ─── Main upload handler ──────────────────────────────────────────

const folderCreationCache = new Map<string, Promise<string>>();

export const getOrCreateFolder = async (
  parentFolderId: string,
  folderName: string,
  accessToken: string,
): Promise<string> => {
  const safeFolderName = sanitizeName(folderName);
  const folderId = parentFolderId?.trim() || "0";
  const cacheKey = `${folderId}|${safeFolderName}`;

  const pending = folderCreationCache.get(cacheKey);
  if (pending) return pending;

  const promise = (async () => {
    const maxRetries = 3;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const entries = await listFolderEntries(folderId, accessToken);
        const existingFolder = entries.find(
          (item) => item.type === "folder" && item.name === safeFolderName,
        );
        if (existingFolder?.id) return String(existingFolder.id);

        const createRes = await fetch("https://api.box.com/2.0/folders", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name: safeFolderName, parent: { id: folderId } }),
        });

        if (createRes.status === 409) {
          const body = await createRes.text();
          const existingId = conflictId(body);
          if (existingId) return existingId;
          continue;
        }

        if (!createRes.ok) {
          throw new Error(
            boxErrorMessage(createRes.status, await createRes.text(), "Could not create folder"),
          );
        }

        const newFolder = await createRes.json();
        return String(newFolder.id);
      } catch (error) {
        if (attempt === maxRetries - 1) throw error;
        await sleep(500 * (attempt + 1));
      }
    }
    throw new Error(`Failed to get/create folder after ${maxRetries} attempts`);
  })();

  folderCreationCache.set(cacheKey, promise);
  promise.catch(() => folderCreationCache.delete(cacheKey));
  return promise;
};

const parentCache = new Map<string, Promise<string>>();

const folderForRelativePath = async (
  rootFolderId: string,
  relativePath: string,
  accessToken: string,
) => {
  const directories = relativePath.split("/").filter(Boolean).slice(0, -1);
  let parent = rootFolderId;

  for (const directory of directories) {
    const safe = sanitizeName(directory);
    const cacheKey = `${parent}|${safe}`;
    const existing = parentCache.get(cacheKey);
    const pending = existing || getOrCreateFolder(parent, safe, accessToken);
    if (!existing) {
      parentCache.set(cacheKey, pending);
      pending.catch(() => parentCache.delete(cacheKey));
    }
    parent = await pending;
  }

  return parent;
};

export const handleBoxUploadWithProgress = async (
  caseOwner: string,
  caseId: string,
  file_type: string,
  caseFolderId: string | null,
  patientFolderName: string | null,
  accessToken: string,
  files: Array<File | BoxUploadItem>,
  onProgress: (sourceKey: string, percent: number) => void,
) => {
  if (!accessToken || !caseId || !file_type) {
    throw new Error("Missing required parameters");
  }

  nameListCache.clear();
  parentCache.clear();

  const uploads = files.map(normalizeUpload);
  let uploadRootId: string;

  if (file_type === "CLIENT_UPLOAD") {
    uploadRootId = caseFolderId || "0";
  } else {
    const tsFolderId = await getOrCreateFolder(caseFolderId || "0", "TS_Uploads", accessToken);
    const targetFolderId = await getOrCreateFolder(
      tsFolderId,
      patientFolderName || caseOwner,
      accessToken,
    );
    uploadRootId =
      file_type.toLowerCase() === "ts_download"
        ? await getOrCreateFolder(targetFolderId, "Downloads", accessToken)
        : await getOrCreateFolder(targetFolderId, "Previews", accessToken);
  }

  const results = await mapPool(uploads, FILE_CONCURRENCY, async (item) => {
    let fileName = item.leafName;

    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        const parentId = await folderForRelativePath(
          uploadRootId,
          item.relativePath,
          accessToken,
        );
        const uniqueName = await reserveUniqueFileName(parentId, fileName, accessToken);
        const boxFileId = await uploadOneFile(
          item.file,
          parentId,
          uniqueName,
          accessToken,
          (percent) => onProgress(item.sourceKey, percent),
        );
        if (!boxFileId) {
          return {
            success: false,
            fileName: uniqueName,
            sourceKey: item.sourceKey,
            error: "Box did not return a file id",
          };
        }
        const parts = item.relativePath.split("/").filter(Boolean);
        parts[parts.length - 1] = uniqueName;
        const storedName = parts
          .map((part, index) => (index === parts.length - 1 ? part : sanitizeName(part)))
          .join("/");
        return {
          success: true,
          boxFileId,
          fileName: storedName.length <= 255 ? storedName : uniqueName,
          sourceKey: item.sourceKey,
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : "Upload failed";
        const nameTaken = /409|item_name_in_use/i.test(message);
        if (nameTaken && attempt < 3) {
          fileName = item.leafName;
          continue;
        }
        return {
          success: false,
          fileName: item.leafName,
          sourceKey: item.sourceKey,
          error: message,
        };
      }
    }

    return {
      success: false,
      fileName: item.leafName,
      sourceKey: item.sourceKey,
      error: "Upload failed",
    };
  });

  return {
    caseId,
    file_type,
    targetFolderName: patientFolderName || caseOwner,
    uploadedFiles: results
      .filter((result) => result.success && result.boxFileId)
      .map((result) => ({
        fileId: result.boxFileId as string,
        fileName: result.fileName,
        sourceKey: result.sourceKey,
      })),
    failedFiles: results
      .filter((result) => !result.success)
      .map((result) => ({
        fileName: result.fileName,
        sourceKey: result.sourceKey,
        error: result.error || "Unknown error",
      })),
  };
};
